// Accordion menu inside the header drawer.
// Stops Dawn's MenuDrawer from treating the accordion <summary> elements as slide-in submenus,
// so the native <details> toggle expands them in place.
if (!customElements.get('accordion-menu')) {
  customElements.define(
    'accordion-menu',
    class AccordionMenu extends HTMLElement {
      connectedCallback() {
        this.addEventListener(
          'click',
          (event) => {
            if (event.target.closest('summary')) event.stopPropagation();
          },
          true
        );

        this.querySelectorAll('details').forEach((details) => {
          details.addEventListener('toggle', () => {
            const summary = details.querySelector(':scope > summary');
            if (summary) summary.setAttribute('aria-expanded', details.open);
          });
        });
      }
    }
  );
}

// Collapsible footer blocks on mobile.
if (!customElements.get('footer-accordion')) {
  customElements.define(
    'footer-accordion',
    class FooterAccordion extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('.footer-accordion__toggle');
        this.content = this.querySelector('.footer-accordion__content');
        if (!this.toggle || !this.content) return;

        this.toggle.addEventListener('click', () => this.setOpen(!this.classList.contains('is-open')));
      }

      setOpen(open) {
        this.classList.toggle('is-open', open);
        this.toggle.setAttribute('aria-expanded', open);
        this.content.style.maxHeight = open ? `${this.content.scrollHeight}px` : null;
      }
    }
  );
}

// Swipe slider for product rows: native scroll-snap on touch, mouse drag on desktop,
// optional arrows and progress bar.
if (!customElements.get('be-slider')) {
  customElements.define(
    'be-slider',
    class BeSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.be-slider__track');
        if (!this.track) return;

        this.prev = this.querySelector('[data-be-slider-prev]');
        this.next = this.querySelector('[data-be-slider-next]');
        this.nav = this.querySelector('.be-slider__nav');
        this.bar = this.querySelector('.be-slider__progress-bar');

        this.update = this.update.bind(this);
        this.track.addEventListener('scroll', this.update, { passive: true });
        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this.track);

        if (this.prev) this.prev.addEventListener('click', () => this.go(-1));
        if (this.next) this.next.addEventListener('click', () => this.go(1));

        this.initDrag();
        this.update();
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      slideStep() {
        const slide = this.track.children[0];
        if (!slide) return this.track.clientWidth;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        return slide.getBoundingClientRect().width + gap;
      }

      go(direction) {
        this.track.scrollBy({ left: direction * this.slideStep(), behavior: 'smooth' });
      }

      update() {
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        const maxScroll = scrollWidth - clientWidth;
        const scrollable = maxScroll > 1;

        if (this.nav) this.nav.classList.toggle('is-hidden', !scrollable);
        if (this.prev) this.prev.disabled = scrollLeft <= 1;
        if (this.next) this.next.disabled = scrollLeft >= maxScroll - 1;

        if (this.bar && scrollable) {
          const visible = clientWidth / scrollWidth;
          this.bar.style.width = `${visible * 100}%`;
          this.bar.style.left = `${(scrollLeft / maxScroll) * (1 - visible) * 100}%`;
        }
      }

      initDrag() {
        let startX = 0;
        let startScroll = 0;
        let moved = false;
        let pointerId = null;

        this.track.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          if (this.track.scrollWidth <= this.track.clientWidth) return;
          pointerId = event.pointerId;
          startX = event.clientX;
          startScroll = this.track.scrollLeft;
          moved = false;
        });

        this.track.addEventListener('pointermove', (event) => {
          if (pointerId !== event.pointerId) return;
          const delta = event.clientX - startX;
          if (!moved && Math.abs(delta) < 5) return;
          if (!moved) {
            moved = true;
            this.track.classList.add('is-dragging');
            this.track.setPointerCapture(pointerId);
          }
          this.track.scrollLeft = startScroll - delta;
        });

        const end = (event) => {
          if (pointerId !== event.pointerId) return;
          pointerId = null;
          if (!moved) return;
          // Re-enabling scroll-snap lets the browser settle on the nearest slide.
          this.track.classList.remove('is-dragging');
          const stop = (clickEvent) => {
            clickEvent.preventDefault();
            clickEvent.stopPropagation();
          };
          this.track.addEventListener('click', stop, { capture: true, once: true });
          setTimeout(() => this.track.removeEventListener('click', stop, { capture: true }), 0);
        };

        this.track.addEventListener('pointerup', end);
        this.track.addEventListener('pointercancel', end);
        this.track.addEventListener('dragstart', (event) => event.preventDefault());
      }
    }
  );
}

// Low stock note with a random number: keeps the same number per product for the session.
document.querySelectorAll('[data-be-stock-random]').forEach((el) => {
  const [min, max] = el.dataset.beStockRandom.split(',').map(Number);
  const key = `be-stock-${location.pathname}`;
  let count;
  try {
    count = Number(sessionStorage.getItem(key));
  } catch (e) {}
  if (!count || count < Math.min(min, max) || count > Math.max(min, max)) {
    count = Math.floor(Math.random() * (Math.abs(max - min) + 1)) + Math.min(min, max);
    try {
      sessionStorage.setItem(key, count);
    } catch (e) {}
  }
  el.querySelectorAll('[data-be-stock-count]').forEach((span) => (span.textContent = count));
});

// Testimonials slider: centered scroll-snap, active (center) card, dots and optional autoplay.
if (!customElements.get('be-testimonials')) {
  customElements.define(
    'be-testimonials',
    class BeTestimonials extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.be-testimonials__track');
        this.slides = Array.from(this.querySelectorAll('.be-testimonials__slide'));
        this.dots = Array.from(this.querySelectorAll('.be-testimonials__dot'));
        if (!this.track || this.slides.length === 0) return;

        this.onScroll = this.onScroll.bind(this);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.index))));

        const start = Math.min(Math.max(Number(this.dataset.start) || 0, 0), this.slides.length - 1);
        requestAnimationFrame(() => {
          this.goTo(start, 'auto');
          this.setActive(start);
        });

        if (this.dataset.autoplay === 'true') {
          this.addEventListener('pointerenter', () => this.stop());
          this.addEventListener('pointerleave', () => this.play());
          this.play();
        }
      }

      disconnectedCallback() {
        this.stop();
      }

      goTo(index, behavior = 'smooth') {
        const slide = this.slides[index];
        if (!slide) return;
        const left = slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2;
        this.track.scrollTo({ left, behavior });
        this.setActive(index);
      }

      onScroll() {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => {
          const center = this.track.scrollLeft + this.track.clientWidth / 2;
          let closest = 0;
          let distance = Infinity;
          this.slides.forEach((slide, index) => {
            const d = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - center);
            if (d < distance) {
              distance = d;
              closest = index;
            }
          });
          this.setActive(closest);
        }, 60);
      }

      setActive(index) {
        this.current = index;
        this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
        this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index));
      }

      play() {
        this.stop();
        const speed = Number(this.dataset.speed) || 5000;
        this.timer = setInterval(() => this.goTo((this.current + 1) % this.slides.length), speed);
      }

      stop() {
        clearInterval(this.timer);
      }
    }
  );
}

// Sticky add-to-cart bar: shows when the main Add to cart button is out of view
// and keeps its own option selects in sync with the variant list.
if (!customElements.get('be-sticky-atc')) {
  customElements.define(
    'be-sticky-atc',
    class BeStickyAtc extends HTMLElement {
      connectedCallback() {
        this.variants = JSON.parse(this.querySelector('[data-be-sticky-variants]')?.textContent || '[]');
        this.strings = JSON.parse(this.querySelector('[data-be-sticky-strings]')?.textContent || '{}');
        this.selects = Array.from(this.querySelectorAll('[data-be-sticky-option]'));
        this.idInput = this.querySelector('input[name="id"]');
        this.button = this.querySelector('.be-sticky-atc__button');
        this.label = this.querySelector('[data-be-sticky-label]');
        this.qtyInput = this.querySelector('input[name="quantity"]');

        this.selects.forEach((select) => select.addEventListener('change', () => this.onOptionChange()));
        this.querySelectorAll('[data-be-sticky-qty]').forEach((btn) =>
          btn.addEventListener('click', () => {
            const value = Math.max(1, (parseInt(this.qtyInput.value) || 1) + Number(btn.dataset.beStickyQty));
            this.qtyInput.value = value;
          })
        );

        this.observeMainButton();
      }

      observeMainButton() {
        const target =
          document.querySelector('product-info .product-form__submit') ||
          document.querySelector('.product-form__submit:not(.be-sticky-atc__button)');
        if (!target) return;

        const observer = new IntersectionObserver(([entry]) => {
          const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', passed);
          this.setAttribute('aria-hidden', !passed);
        });
        observer.observe(target);
        this.observer = observer;
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      onOptionChange() {
        const selected = this.selects.map((select) => select.value);
        const variant = this.variants.find((v) => v.options.every((option, index) => option === selected[index]));

        if (!variant) {
          this.button.disabled = true;
          this.label.textContent = this.strings.unavailable;
          return;
        }

        this.idInput.value = variant.id;
        this.button.disabled = !variant.available;
        this.label.textContent = variant.available ? this.strings.add : this.strings.soldOut;

        const price = this.querySelector('[data-be-sticky-price]');
        const compare = this.querySelector('[data-be-sticky-compare]');
        if (price) price.textContent = this.formatMoney(variant.price);
        if (compare) compare.textContent = variant.compare > variant.price ? this.formatMoney(variant.compare) : '';

        const image = this.querySelector('.be-sticky-atc__img');
        if (image && variant.image) {
          image.src = variant.image;
          image.removeAttribute('srcset');
        }
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const value = (cents / 100).toFixed(2);
        const [whole, decimals] = value.split('.');
        const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const withDots = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
          switch (key) {
            case 'amount_no_decimals':
              return withCommas;
            case 'amount_with_comma_separator':
              return `${withDots},${decimals}`;
            case 'amount_no_decimals_with_comma_separator':
              return withDots;
            default:
              return `${withCommas}.${decimals}`;
          }
        });
      }
    }
  );
}
